/**
 * Crypto helpers — client-safe (Web Crypto API).
 *
 * Multi-runtime: uses `globalThis.crypto.getRandomValues` + `crypto.subtle.digest`.
 * Works in browsers (HTTPS/localhost) and in Node ≥ 19. React Native requires
 * polyfills (`react-native-get-random-values` plus a native SHA-256 from
 * `react-native-quick-crypto` or `expo-crypto`).
 *
 * Once the module is extracted as a library this file stays as the default
 * entry; specialised runtimes (Node-only) inject variants via subpath exports.
 */

/** Encode bytes as base64url (RFC 4648 §5) without depending on Buffer. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]);
  const base64 =
    typeof btoa === 'function'
      ? btoa(binary)
      : // eslint-disable-next-line @typescript-eslint/no-require-imports
        Buffer.from(binary, 'binary').toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Cryptographically strong PKCE code verifier (32 bytes → 43-char base64url). */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  globalThis.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/** SHA-256(verifier) in base64url — the PKCE `code_challenge`. */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  // Browsers expose `crypto.subtle` only in a secure context, and plain HTTP
  // counts as one only for loopback hosts. Serving dev over `http://` on a
  // non-loopback hostname therefore leaves `subtle` undefined and login fails
  // before its first request with an opaque TypeError. Say so instead.
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      'Web Crypto (crypto.subtle) is unavailable, so the PKCE code challenge cannot be computed. ' +
        'This usually means the page is not a secure context — serve it over HTTPS (or localhost).',
    );
  }

  const data = new TextEncoder().encode(verifier);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Cryptographically strong CSRF state — 16 bytes → 32-char hex (≥128 bits). */
export function generateState(): string {
  const array = new Uint8Array(16);
  globalThis.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
}
